import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingCart, User, LogOut, FlaskConical } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { to: "/shop", label: "Catalog" },
  { to: "/lab-testing", label: "Laboratory Testing" },
  // { to: "/coa-library", label: "COA Library" }, // HIDDEN: uncomment to restore
  // { to: "/research", label: "Research Library" }, // HIDDEN: uncomment to restore
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();

  return (
    <header className="no-print sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display" data-testid="nav-logo-link">
          <div className="relative">
            <FlaskConical className="h-6 w-6 text-brandblue-600 relative z-10" strokeWidth={1.5} />
            <div className="absolute inset-0 bg-brandblue-500 blur-md opacity-30 rounded-full" />
          </div>
          <div className="leading-tight">
            <p className="text-lg font-bold text-slate-900 tracking-tight">True Impact Peptides</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-brandblue-600 font-semibold">Precision Science. Pure Results.</p>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors py-1 ${isActive ? "text-brandblue-700" : "text-slate-600 hover:text-brandblue-600"} after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:bg-gradient-to-r after:from-blue-900 after:to-blue-500 after:transition-all after:duration-300 ${isActive ? "after:w-full" : "after:w-0 hover:after:w-full"}`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" data-testid="nav-account-btn" onClick={() => navigate(user.role === "admin" ? "/admin" : "/account")} className="hover:text-brandblue-700">
                <User className="h-4 w-4 mr-1.5" /> {user.role === "admin" ? "Admin" : "Account"}
              </Button>
              <Button variant="ghost" size="sm" data-testid="nav-logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="hover:text-brandblue-700">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button variant="ghost" size="sm" data-testid="nav-login-btn" onClick={() => navigate("/login")} className="hover:text-brandblue-700">
              <User className="h-4 w-4 mr-1.5" /> Research Login
            </Button>
          )}
          <Link to="/cart" className="relative" data-testid="nav-cart-link">
            <Button size="sm" className="rounded-full btn-lift">
              <ShoppingCart className="h-4 w-4 mr-1.5" /> Cart
              {itemCount > 0 && (
                <span className="ml-1.5 bg-white/25 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse-glow" data-testid="nav-cart-count">
                  {itemCount}
                </span>
              )}
            </Button>
          </Link>
        </div>

        <button className="lg:hidden text-slate-700" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-6 py-4 space-y-3" data-testid="mobile-menu">
          {NAV_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className="block text-sm font-medium text-slate-700" onClick={() => setOpen(false)}>
              {link.label}
            </Link>
          ))}
          <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
            {user ? (
              <>
                <Button variant="outline" size="sm" onClick={() => { setOpen(false); navigate(user.role === "admin" ? "/admin" : "/account"); }}>Account</Button>
                <Button variant="outline" size="sm" onClick={async () => { await logout(); setOpen(false); navigate("/"); }}>Logout</Button>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={() => { setOpen(false); navigate("/login"); }}>Research Login</Button>
            )}
            <Link to="/cart" onClick={() => setOpen(false)}>
              <Button size="sm" className="rounded-full btn-lift">Cart ({itemCount})</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
